import { useEffect, useState, type FormEvent } from 'react';
import {
  Alert,
  Select,
  V2Button,
  V2FormActions,
  V2FormField,
  V2FormGrid,
  V2Modal,
} from '@sakhtar/design-system/react';
import { apiPatch, ApiError } from '../../lib/apiClient.js';
import type { CustomerSummary } from '../../lib/types/customers.js';

/**
 * Edit identity fields in a dialog (bottom sheet on phones) instead of an
 * inline card that pushed the whole page down. While there are unsaved
 * changes the dialog ignores Esc/backdrop so an accidental click cannot
 * discard them; «انصراف» is the explicit way out.
 */
export function CustomerEditDialog({
  customer,
  base,
  onSaved,
  onCancel,
}: {
  readonly customer: CustomerSummary;
  readonly base: string;
  readonly onSaved: (customer: CustomerSummary) => void;
  readonly onCancel: () => void;
}) {
  const [code, setCode] = useState(customer.ref ?? '');
  const [name, setName] = useState(customer.name ?? '');
  const [kind, setKind] = useState<'person' | 'organization'>(customer.kind ?? 'organization');
  const [status, setStatus] = useState<'active' | 'inactive'>(customer.status ?? 'active');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);

  const dirty =
    code !== (customer.ref ?? '') ||
    name !== (customer.name ?? '') ||
    kind !== (customer.kind ?? 'organization') ||
    status !== (customer.status ?? 'active');
  const codeMissing = touched && code.trim() === '';
  const nameMissing = touched && name.trim() === '';

  useEffect(() => {
    const guard = (event: BeforeUnloadEvent) => {
      if (!dirty) return;
      event.preventDefault();
    };
    window.addEventListener('beforeunload', guard);
    return () => window.removeEventListener('beforeunload', guard);
  }, [dirty]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setTouched(true);
    if (!customer.ref || code.trim() === '' || name.trim() === '') return;
    setBusy(true);
    setError(null);
    try {
      const updated = await apiPatch<CustomerSummary>(
        `${base}/api/v1/crm/customers/${encodeURIComponent(customer.ref)}`,
        { code: code.trim(), name: name.trim(), kind, status },
      );
      onSaved(updated);
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.envelope.error.message : 'ویرایش مشتری ناموفق بود.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <V2Modal open title="ویرایش مشتری" size="md" dismissible={!dirty && !busy} onClose={onCancel}>
      <form noValidate onSubmit={submit}>
        <V2FormGrid>
          <V2FormField
            label="کد مشتری"
            required
            width="short"
            error={codeMissing ? 'کد مشتری را وارد کنید.' : undefined}
          >
            <input
              className="sk-input ltr"
              value={code}
              onChange={(event) => setCode(event.target.value)}
              aria-invalid={codeMissing || undefined}
            />
          </V2FormField>
          <V2FormField
            label="نام"
            required
            width="wide"
            error={nameMissing ? 'نام مشتری را وارد کنید.' : undefined}
          >
            <input
              className="sk-input"
              value={name}
              onChange={(event) => setName(event.target.value)}
              aria-invalid={nameMissing || undefined}
            />
          </V2FormField>
          <V2FormField label="نوع" width="medium">
            <Select value={kind} onChange={(event) => setKind(event.target.value as typeof kind)}>
              <option value="organization">سازمان</option>
              <option value="person">شخص</option>
            </Select>
          </V2FormField>
          <V2FormField label="وضعیت" width="medium">
            <Select
              value={status}
              onChange={(event) => setStatus(event.target.value as typeof status)}
            >
              <option value="active">فعال</option>
              <option value="inactive">غیرفعال</option>
            </Select>
          </V2FormField>
        </V2FormGrid>
        {error ? (
          <Alert tone="danger" title="خطا">
            {error}
          </Alert>
        ) : null}
        <V2FormActions>
          <V2Button type="button" variant="ghost" onClick={onCancel} disabled={busy}>
            انصراف
          </V2Button>
          <V2Button type="submit" variant="primary" disabled={!dirty || busy}>
            {busy ? 'در حال ذخیره…' : 'ذخیره تغییرات'}
          </V2Button>
        </V2FormActions>
      </form>
    </V2Modal>
  );
}
